import type { UnavailableHours } from "../../ChargerForm/UnavailableTimesGrid";

export type UnavailableTimesJSON = {
  [day: string]: number[];
} | null;

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const ALL_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// ── Serialization ────────────────────────────────────────────────────────────

/**
 * Convert the grid's boolean-array format to the compact JSON format
 * used by the API (arrays of unavailable hour integers per day).
 */
export const unavailableToJSON = (
  hours: UnavailableHours
): UnavailableTimesJSON => {
  const result: { [day: string]: number[] } = {};
  for (const [day, booleans] of Object.entries(hours)) {
    result[day] = booleans.reduce<number[]>((acc, val, idx) => {
      if (val) acc.push(idx);
      return acc;
    }, []);
  }
  const hasAny = Object.values(result).some((arr) => arr.length > 0);
  return hasAny ? result : null;
};

/**
 * Convert the API JSON format back into the grid's boolean-array format.
 */
export const jsonToUnavailable = (
  json: UnavailableTimesJSON
): UnavailableHours => {
  const result: UnavailableHours = {};
  ALL_DAYS.forEach((day) => {
    result[day] = new Array(24).fill(false);
    if (json?.[day]) {
      json[day].forEach((hour) => {
        if (hour >= 0 && hour < 24) result[day][hour] = true;
      });
    }
  });
  return result;
};

// ── Runtime checks ───────────────────────────────────────────────────────────

/**
 * Check whether a charger is unavailable at a given moment.
 * Uses the device's local time by default.
 */
export const isCurrentlyUnavailable = (
  unavailableTimes: UnavailableTimesJSON,
  date: Date = new Date()
): boolean => {
  if (!unavailableTimes) return false;
  const dayName = DAY_NAMES[date.getDay()];
  const hour = date.getHours();
  const hours = unavailableTimes[dayName];
  if (!hours || !Array.isArray(hours)) return false;
  return hours.includes(hour);
};

/**
 * Return info about the next unavailability transition within the given
 * look-ahead window (default 60 minutes).
 *
 * Returns `null` if there is no transition within the window.
 */
export const getNextUnavailableTransition = (
  unavailableTimes: UnavailableTimesJSON,
  lookaheadMinutes: number = 60,
  date: Date = new Date()
): {
  becomesUnavailable: boolean;
  at: Date;
  minutesUntil: number;
} | null => {
  if (!unavailableTimes) return null;

  const currentlyUnavail = isCurrentlyUnavailable(unavailableTimes, date);

  for (let m = 1; m <= lookaheadMinutes; m++) {
    const future = new Date(date.getTime() + m * 60_000);
    const futureUnavail = isCurrentlyUnavailable(unavailableTimes, future);
    if (futureUnavail !== currentlyUnavail) {
      return {
        becomesUnavailable: futureUnavail,
        at: future,
        minutesUntil: m,
      };
    }
  }
  return null;
};

/**
 * Check whether a scheduled time range overlaps with any unavailable hours.
 * Returns conflicting hours grouped by day, or null if no conflicts.
 */
export const getScheduleConflicts = (
  unavailableTimes: UnavailableTimesJSON,
  startTime: Date,
  endTime: Date
): { day: string; hours: number[]; date: string }[] | null => {
  if (!unavailableTimes) return null;

  const conflicts: { day: string; hours: number[]; date: string }[] = [];
  const cursor = new Date(startTime);

  while (cursor < endTime) {
    const dayName = DAY_NAMES[cursor.getDay()];
    const hour = cursor.getHours();
    const dateStr = cursor.toISOString().split("T")[0];

    if (unavailableTimes[dayName]?.includes(hour)) {
      const existing = conflicts.find(
        (c) => c.day === dayName && c.date === dateStr
      );
      if (existing) {
        if (!existing.hours.includes(hour)) existing.hours.push(hour);
      } else {
        conflicts.push({ day: dayName, hours: [hour], date: dateStr });
      }
    }

    cursor.setTime(cursor.getTime() + 3_600_000);
  }

  return conflicts.length > 0 ? conflicts : null;
};

/**
 * Get a human-readable summary of the next unavailability event.
 * Useful for badges and status text.
 */
export const getUnavailabilitySummary = (
  unavailableTimes: UnavailableTimesJSON,
  date: Date = new Date()
): string | null => {
  if (!unavailableTimes) return null;

  const currentlyUnavail = isCurrentlyUnavailable(unavailableTimes, date);

  if (currentlyUnavail) {
    const transition = getNextUnavailableTransition(
      unavailableTimes,
      24 * 60,
      date
    );
    if (transition && !transition.becomesUnavailable) {
      const timeStr = transition.minutesUntil >= 60
        ? `${(transition.minutesUntil / 60).toFixed(1)} hrs`
        : `${transition.minutesUntil} min`;
      return `Available again in ${timeStr}`;
    }
    return "Currently unavailable";
  }

  const transition = getNextUnavailableTransition(unavailableTimes, 60, date);
  if (transition && transition.becomesUnavailable) {
    const timeStr = transition.minutesUntil >= 60
      ? `${(transition.minutesUntil / 60).toFixed(1)} hrs`
      : `${transition.minutesUntil} min`;
    return `Unavailable in ${timeStr}`;
  }

  return null;
};
